<template>
  <transition name="filter-responsibilities-modal" appear>
    <div
      v-if="value"
      class="filter-responsibilities-modal__overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="filter-responsibilities-modal-title"
      @mousedown.self="handleClose"
    >
      <div class="filter-responsibilities-modal__card">
        <div class="filter-responsibilities-modal__header">
          <span
            id="filter-responsibilities-modal-title"
            class="filter-responsibilities-modal__title"
          >
            Filter responsibilities
          </span>
          <button
            type="button"
            class="filter-responsibilities-modal__close-btn"
            aria-label="Close"
            @click="handleClose"
          >
            <Icon name="popup_close" />
          </button>
        </div>

        <div class="filter-responsibilities-modal__filters-row">
          <div class="filter-responsibilities-modal__column">
            <span class="filter-responsibilities-modal__column-title">Status</span>
            <div class="filter-responsibilities-modal__options">
              <CheckboxInput
                v-for="option in statusOptions"
                :key="option.value"
                :label="option.label"
                :value="statuses[option.value]"
                @input="updateStatus(option.value, $event)"
              />
            </div>
          </div>

          <div
            v-for="group in firstRowGroups"
            :key="group.key"
            class="filter-responsibilities-modal__column filter-responsibilities-modal__column--radio"
          >
            <RadioInput
              :label="group.label"
              :availableItems="group.items"
              :checkedItem="checkedItemFor(group)"
              @changeradio="onRadioChange(group, $event)"
            />
          </div>
        </div>

        <div class="filter-responsibilities-modal__divider" />

        <div class="filter-responsibilities-modal__filters-row">
          <div
            v-for="group in secondRowGroups"
            :key="group.key"
            class="filter-responsibilities-modal__column filter-responsibilities-modal__column--radio"
          >
            <RadioInput
              :label="group.label"
              :availableItems="group.items"
              :checkedItem="checkedItemFor(group)"
              @changeradio="onRadioChange(group, $event)"
            />
          </div>
        </div>

        <div class="filter-responsibilities-modal__footer">
          <button
            type="button"
            class="filter-responsibilities-modal__reset"
            @click="handleReset"
          >
            Reset to defaults
          </button>
          <StandardButton
            type="magenta"
            variant="primary"
            size="small"
            label="Apply"
            @click="handleApply"
          />
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted } from "vue";
import Icon from "@components/icons/Icon.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import CheckboxInput from "@components/Checkbox/CheckboxInput.vue";
import RadioInput from "@components/Radio/RadioInput.vue";
import {
  DEFAULT_RESPONSIBILITY_FILTER_PAYLOAD as DEFAULTS,
  ARCHIVED_LABEL_BY_VALUE,
  CHECKLIST_LABEL_BY_VALUE,
  ROLES_LABEL_BY_VALUE,
  USAGE_LABEL_BY_VALUE,
  COVERAGE_LABEL_BY_VALUE,
  TIMELINE_LABEL_BY_VALUE,
} from "./responsibilityFilterPayload";

const props = defineProps({
  value: { type: Boolean, default: false },
  initialStatuses: {
    type: Object,
    default: () => ({ ...DEFAULTS.statuses }),
  },
  initialArchived: { type: String, default: DEFAULTS.archived },
  initialChecklist: { type: String, default: DEFAULTS.checklist },
  initialRoles: { type: String, default: DEFAULTS.roles },
  initialUsage: { type: String, default: DEFAULTS.usage },
  initialCoverage: { type: String, default: DEFAULTS.coverage },
  initialTimeline: { type: String, default: DEFAULTS.timeline },
});

const emit = defineEmits(["input", "close", "apply", "reset"]);

const statusOptions = [
  { label: "Draft", value: "draft" },
  { label: "Active", value: "active" },
  { label: "Retired", value: "retired" },
];

const NO_FILTER_LABEL = "No Filter";

function buildGroup(key, label, labelByValue, { withNoFilter = true } = {}) {
  const items = Object.values(labelByValue).map((itemLabel) => ({ label: itemLabel }));
  return {
    key,
    label,
    labelByValue,
    items: withNoFilter ? [{ label: NO_FILTER_LABEL }, ...items] : items,
  };
}

const archivedGroup = buildGroup("archived", "Archived", ARCHIVED_LABEL_BY_VALUE, {
  withNoFilter: false,
});
const firstRowGroups = [
  archivedGroup,
  buildGroup("checklist", "Checklist", CHECKLIST_LABEL_BY_VALUE),
];
const secondRowGroups = [
  buildGroup("roles", "Roles", ROLES_LABEL_BY_VALUE),
  buildGroup("usage", "Usage", USAGE_LABEL_BY_VALUE),
  buildGroup("coverage", "Coverage", COVERAGE_LABEL_BY_VALUE),
  buildGroup("timeline", "Timeline", TIMELINE_LABEL_BY_VALUE),
];

const propInitialByKey = {
  archived: () => props.initialArchived,
  checklist: () => props.initialChecklist,
  roles: () => props.initialRoles,
  usage: () => props.initialUsage,
  coverage: () => props.initialCoverage,
  timeline: () => props.initialTimeline,
};

const statuses = ref({ ...props.initialStatuses });
const selection = ref({});

function readSelectionFromProps() {
  const next = {};
  Object.keys(propInitialByKey).forEach((key) => {
    next[key] = propInitialByKey[key]();
  });
  return next;
}

selection.value = readSelectionFromProps();

watch(
  () => props.value,
  (open) => {
    if (open) syncFromProps();
  },
  { immediate: true }
);

function syncFromProps() {
  statuses.value = { ...props.initialStatuses };
  selection.value = readSelectionFromProps();
}

function checkedItemFor(group) {
  return group.labelByValue[selection.value[group.key]] || NO_FILTER_LABEL;
}

function onRadioChange(group, payload) {
  const entry = Object.entries(group.labelByValue).find(([, label]) => label === payload?.value);
  selection.value = { ...selection.value, [group.key]: entry ? entry[0] : DEFAULTS[group.key] };
}

function updateStatus(key, checked) {
  statuses.value = { ...statuses.value, [key]: !!checked };
}

function handleReset() {
  statuses.value = { ...DEFAULTS.statuses };
  const next = {};
  Object.keys(propInitialByKey).forEach((key) => {
    next[key] = DEFAULTS[key];
  });
  selection.value = next;
  emit("reset");
}

function handleApply() {
  emit("apply", { statuses: { ...statuses.value }, ...selection.value });
}

function handleClose() {
  emit("input", false);
  emit("close");
}

function handleKeydown(event) {
  if (event.key === "Escape" && props.value) handleClose();
}

onMounted(() => {
  document.addEventListener("keydown", handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeydown);
});
</script>

<style scoped>
.filter-responsibilities-modal-enter-active,
.filter-responsibilities-modal-leave-active {
  transition: opacity 180ms ease;
}

.filter-responsibilities-modal-enter-from,
.filter-responsibilities-modal-leave-to {
  opacity: 0;
}

.filter-responsibilities-modal-enter-active .filter-responsibilities-modal__card,
.filter-responsibilities-modal-leave-active .filter-responsibilities-modal__card {
  transition:
    transform 200ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 180ms ease;
}

.filter-responsibilities-modal-enter-from .filter-responsibilities-modal__card,
.filter-responsibilities-modal-leave-to .filter-responsibilities-modal__card {
  transform: translateY(8px) scale(0.98);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .filter-responsibilities-modal-enter-active,
  .filter-responsibilities-modal-leave-active,
  .filter-responsibilities-modal-enter-active .filter-responsibilities-modal__card,
  .filter-responsibilities-modal-leave-active .filter-responsibilities-modal__card {
    transition: none;
  }
}

.filter-responsibilities-modal__overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(16, 24, 40, 0.5);
  font-family: Inter, sans-serif;
}

.filter-responsibilities-modal__card {
  width: 100%;
  max-width: 1032px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow:
    0 8px 8px -4px rgba(16, 24, 40, 0.03),
    0 20px 24px -4px rgba(16, 24, 40, 0.08);
  box-sizing: border-box;
}

.filter-responsibilities-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.filter-responsibilities-modal__title {
  color: #101828;
  font-size: 18px;
  font-weight: 600;
  line-height: normal;
}

.filter-responsibilities-modal__close-btn {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 4px;
  margin: -4px;
  cursor: pointer;
  color: #667085;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.filter-responsibilities-modal__close-btn:hover {
  background: rgba(152, 162, 179, 0.12);
}

.filter-responsibilities-modal__close-btn:focus-visible {
  outline: 2px solid #667085;
  outline-offset: 2px;
}

.filter-responsibilities-modal__filters-row {
  display: flex;
  gap: 32px;
  align-items: flex-start;
  width: 100%;
}

.filter-responsibilities-modal__column {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-responsibilities-modal__column-title {
  padding-left: 8px;
  color: #344054;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.filter-responsibilities-modal__options {
  display: flex;
  flex-direction: column;
  padding-left: 8px;
}

.filter-responsibilities-modal__divider {
  height: 1px;
  width: 100%;
  background: #e5e7eb;
}

.filter-responsibilities-modal__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.filter-responsibilities-modal__reset {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 4px;
  margin: -4px;
  color: #475467;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: normal;
  cursor: pointer;
  border-radius: 6px;
}

.filter-responsibilities-modal__reset:hover {
  color: #344054;
  background: rgba(152, 162, 179, 0.08);
}

.filter-responsibilities-modal__reset:focus-visible {
  outline: 2px solid #667085;
  outline-offset: 2px;
}
</style>
